// @ts-nocheck
import Typography from "@store/MobiusOverrides/Typography/Typography";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import siteMessage from "@insite/client-framework/SiteMessage";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useBunzlDispatch } from "@store/Framework/Store/Bunzl/BunzlReducer";
import submitCreditForm from "@store/Framework/Store/Data/CreditForm/SubmitCreditForm";
import CreditFormButtons from "@store/Library/Components/CreditForm/CreditFormButtons";
import BankingDetailsForm from "@store/Library/Components/CreditForm/StepComponents/BankingDetailsForm";
import BasicDetailsForm from "@store/Library/Components/CreditForm/StepComponents/BasicDetailsForm";
import CreditFormConfirmForm from "@store/Library/Components/CreditForm/StepComponents/CreditFormConfirmForm";
import PrincipalAndTaxInformationForm from "@store/Library/Components/CreditForm/StepComponents/PrincipalAndTaxInformationForm";
import TradeReferencesForm from "@store/Library/Components/CreditForm/StepComponents/TradeReferencesForm";
import FadeIn from "@store/Library/Components/Reporting/Common/FadeIn";
import CardComponent, { CardStyles } from "@store/Library/Widgets/Card/Card";
import CardFooterComponent, { CardFooterStyles } from "@store/Library/Widgets/Card/CardFooter";
import CardHeaderComponent from "@store/Library/Widgets/Card/CardHeader";
import { useCreditFormContext } from "@store/Library/Widgets/CreditForm/CreditFormContextContainer";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { toToolkitApplicationState } from "@toolkit/Framework/Store/ApplicationState";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

interface CreditFormFormProps {}
interface CreditFormFormStyles {
    description?: InjectableCss;
    cardComponentStyles?: CardStyles;
    cardFooterStyles?: CardFooterStyles;
}
export interface TermsSignatureData {
    firstName: string;
    lastName: string;
    signedAt: string;
    signature: string;
    position: string;
    email: string;
}
const creditFormFormStyles: CreditFormFormStyles = {
    description: {
        css: css`
            opacity: 0.4;
            color: black;
        `,
    },
    cardComponentStyles: {
        css: css`
            height: 100%;
            border-radius: ${pxToRem(12)};
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            background: ${getColor("common.backgroundContrastLight")};
        `,
    },
    cardFooterStyles: {
        css: css`
            margin-top: auto;
        `,
    },
};

const CreditFormForm: React.FC<CreditFormFormProps> = () => {
    const { steps, currentStep: currentStepIndex, setCurrentStep, saveForm, link } = useCreditFormContext();
    const url = useGetLink(link).url;
    const [termsSignatureData, setTermsSignatureData] = React.useState<TermsSignatureData | undefined>();
    const [hasSubmitted, setHasSubmitted] = React.useState(false);
    const dispatch = useDispatch();
    const bunzlDispatch = useBunzlDispatch();
    const isFormValid = useAppSelector(
        state => toToolkitApplicationState(state).bunzl.pages.stepForm?.creditForm?.isFormValid,
    );
    const history = useHistory();
    const toaster = useToaster();
    useEffect(() => {
        const formTopElement = document.getElementById("form-top");
        if (formTopElement) {
            formTopElement.scrollIntoView(true);
        }
    }, [currentStepIndex]);
    const handleFormSave = () => {
        saveForm();
        toaster.addToast({ body: siteMessage("CreditForm_Save_Message"), messageType: "success" });
    };
    const handleCurrentStepSubmit = () => {
        setCurrentStep(currentStepIndex + 1);
    };
    const handleOnSuccess = () => {
        toaster.addToast({ body: siteMessage("CreditForm_Success_Message"), messageType: "success" });
        bunzlDispatch({
            type: "Data/StepForm/ClearData",
            formKey: "creditForm",
        });
        if (url) {
            history.push(url);
        }
    };
    const handleFormSubmit = () => {
        saveForm();

        if (!termsSignatureData) {
            return;
        }

        dispatch(
            submitCreditForm({
                signatureData: termsSignatureData,
                onSuccess: handleOnSuccess,
                onError: () =>
                    toaster.addToast({ body: siteMessage("CreditForm_Error_Message"), messageType: "danger" }),
            }),
        );
    };
    const onNextStep = currentStepIndex < steps.length - 1 ? handleCurrentStepSubmit : handleFormSubmit;
    const nextStepDisabled = currentStepIndex === steps.length - 1 ? !termsSignatureData : false;
    const previousStepDisabled = currentStepIndex === 0;
    const onPreviousStep = currentStepIndex > 0 ? () => setCurrentStep(currentStepIndex - 1) : undefined;
    const currentStep = steps[currentStepIndex];

    return (
        <CardComponent
            extendedStyles={creditFormFormStyles.cardComponentStyles}
            justify-content="space-between"
            variant="unstyled"
            size="large"
        >
            <FadeIn key={currentStepIndex} duration={100} animate appear>
                <CardHeaderComponent>
                    {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                    <Typography id="form-top" variant="h3">
                        {translate(currentStep.label)}
                    </Typography>

                    <Typography {...creditFormFormStyles.description} variant="h5">
                        {currentStep.description}
                    </Typography>
                </CardHeaderComponent>
                {currentStep.label === "Basic Details" && (
                    <BasicDetailsForm stepIndex={currentStepIndex} hasSubmitted={hasSubmitted} />
                )}
                {currentStep.label === "Principal and Tax Information" && (
                    <PrincipalAndTaxInformationForm stepIndex={currentStepIndex} hasSubmitted={hasSubmitted} />
                )}
                {currentStep.label === "Trade References" && (
                    <TradeReferencesForm stepIndex={currentStepIndex} hasSubmitted={hasSubmitted} />
                )}
                {currentStep.label === "Banking Information" && (
                    <BankingDetailsForm stepIndex={currentStepIndex} hasSubmitted={hasSubmitted} />
                )}
                {currentStep.label === "Review and Submit" && (
                    <CreditFormConfirmForm
                        setSignatureData={setTermsSignatureData}
                        isFormValid={isFormValid}
                        setHasSubmitted={setHasSubmitted}
                        hasSubmitted={hasSubmitted}
                        step={steps[4]}
                    />
                )}
            </FadeIn>
            <CardFooterComponent extendedStyles={creditFormFormStyles.cardFooterStyles} justifyContent="space-between">
                <CreditFormButtons
                    finalStep={currentStepIndex === steps.length - 1}
                    onNextStep={onNextStep}
                    onFormSave={handleFormSave}
                    previousStepDisabled={previousStepDisabled}
                    nextStepDisabled={nextStepDisabled}
                    onPreviousStep={onPreviousStep}
                />
            </CardFooterComponent>
        </CardComponent>
    );
};

const widgetModule: WidgetModule = {
    component: CreditFormForm,
    definition: {
        group: "Common",
        displayName: "Credit Form Form",
    },
};

export default widgetModule;
